/*
  SOFI: in-place department navigation on collection pages.

  Clicking a department in the sidebar, the breadcrumbs or the phone chip row fetches
  the collection banner and product-grid sections (Shopify Section Rendering API:
  /collections/<handle>?sections=<banner>,<grid>). The sidebar itself is NOT replaced:
  the banner (title, crumbs, chips) is swapped, and from the grid section only the
  products, filters and count are applied (with facets.js's own render helpers) plus the
  contents of the two department blocks. So nothing in the sidebar jumps or redraws
  around the shopper. The address bar updates quietly (history.pushState); Back/Forward
  swap the same way. Page links inside the grid also load in place (facets.js renderPage).
  Anything unexpected (error, bad response, 12 s timeout) falls back to a normal load.

  Loaded by sections/main-collection-product-grid.liquid (vertical filter layout).
  facets.js stays stock. This file keeps it in step after a swap:
  - FacetFiltersForm.searchParamsPrev / searchParamsInitial follow the new department;
  - a filter render that started before the swap (debounced submit from a form that
    is no longer on the page, or a fetch still in flight) is dropped, not drawn over
    the new department.
*/
(function () {
  if (window.__sofiDeptNav) return;
  window.__sofiDeptNav = true;
  if (!window.fetch || !window.DOMParser || !window.history || !history.pushState) return;

  var LINK_SELECTOR = '.sofi-dept a[href], .sofi-crumbs a[href], .sofi-kids a[href]';
  var PAGE_LINK_SELECTOR = '#ProductGridContainer .pagination a[href]';
  var COLLECTION_PATH = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^\/]+\/?$/i;
  var TIMEOUT_MS = 12000;

  function sectionOf(el) {
    var section = el && el.closest('.shopify-section');
    return section && section.id.indexOf('shopify-section-') === 0 ? section : null;
  }
  function getSections() {
    var banner = sectionOf(document.querySelector('.collection-hero'));
    var grid = sectionOf(document.getElementById('ProductGridContainer'));
    if (!banner || !grid || banner === grid) return null;
    return {
      banner: banner,
      grid: grid,
      bannerId: banner.id.replace('shopify-section-', ''),
      gridId: grid.id.replace('shopify-section-', ''),
    };
  }

  if (!getSections()) return;

  var renderedPath = location.pathname.replace(/\/$/, '');
  var pending = null; // { controller, timer, timedOut }
  var swapGeneration = 0;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Give the first history entry a state, so Back to it is handled like the others.
  try {
    history.replaceState(
      Object.assign({}, history.state || {}, {
        searchParams: location.search.slice(1),
        sofiPath: location.pathname,
      }),
      ''
    );
  } catch (e) {}

  /* ---------- facets.js guards ---------- */
  function hasFacets() {
    return typeof FacetFiltersForm !== 'undefined';
  }

  function syncFacets(searchParams) {
    if (!hasFacets()) return;
    FacetFiltersForm.searchParamsPrev = searchParams;
    FacetFiltersForm.searchParamsInitial = searchParams;
  }

  function patchFacets() {
    if (!hasFacets() || FacetFiltersForm.__sofiPatched) return;
    FacetFiltersForm.__sofiPatched = true;

    // A debounced submit from a filter form that a swap has removed: ignore it.
    var originalRenderPage = FacetFiltersForm.renderPage;
    FacetFiltersForm.renderPage = function (searchParams, event, updateURLHash) {
      var target = event && event.target;
      if (target && target.nodeType === 1 && !target.isConnected) return;
      // A filter/sort change made while a department is loading belongs to the old
      // department: ignore it, the incoming department brings its own filters.
      if (event && pending) return;
      return originalRenderPage.apply(FacetFiltersForm, arguments);
    };

    // A filter fetch that was in flight when a swap happened: cache it, don't draw it.
    FacetFiltersForm.renderSectionFromFetch = function (url, event) {
      var generation = swapGeneration;
      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(function (html) {
          FacetFiltersForm.filterData = FacetFiltersForm.filterData.concat([{ html: html, url: url }]);
          if (generation !== swapGeneration) return;
          FacetFiltersForm.renderFilters(html, event);
          FacetFiltersForm.renderProductGridContainer(html);
          FacetFiltersForm.renderProductCount(html);
          if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        });
    };
  }
  // facets.js is deferred like this file and loads first; patch now and again on load to be safe.
  patchFacets();
  window.addEventListener('DOMContentLoaded', patchFacets);

  /* ---------- page state helpers ---------- */
  function closePanel() {
    document.querySelectorAll('[data-sofi-dside].is-open').forEach(function (side) {
      side.classList.remove('is-open');
      var opener = side.querySelector('[data-sofi-dside-open]');
      if (opener) opener.setAttribute('aria-expanded', 'false');
    });
    document.documentElement.classList.remove('sofi-dside-lock');
  }

  // Dim only what will change (title area + products), never the sidebar.
  function setLoading(on) {
    var sections = getSections();
    if (!sections) return;
    sections.banner.classList.toggle('sofi-dept-loading', on);
    var grid = document.getElementById('ProductGridContainer');
    if (grid) grid.classList.toggle('sofi-dept-loading', on);
  }

  /*
    Apply the fetched product-grid section without replacing the sidebar element:
    the CONTENTS of the filter container (desktop form + phone drawer), the toolbar
    (sort + count), the product grid and the two department blocks are replaced.
    Containers are swapped whole because each department can have a different set of
    filters. Everything is checked first, then mutated, so a missing piece falls back to
    replacing the whole section (the earlier behaviour) and never leaves a mixed page.
  */
  var GRID_PARTS = [
    '#ProductGridContainer',
    '.facets-wrapper > .facets-container',
    '.facets-vertical-sort',
    '[data-sofi-dept]',
    '[data-sofi-dept-all]',
  ];
  function applyGrid(sections, raw) {
    var doc = new DOMParser().parseFromString(raw, 'text/html');
    var pairs = [];
    var ok = true;
    GRID_PARTS.forEach(function (selector) {
      var here = sections.grid.querySelector(selector);
      var there = doc.querySelector(selector);
      if (!here && !there) return; // not part of this layout
      if (!here || !there) ok = false;
      else pairs.push([here, there]);
    });
    if (ok && pairs.length) {
      pairs.forEach(function (pair) {
        pair[0].innerHTML = pair[1].innerHTML;
      });
      return true;
    }
    var inner = innerOf(raw, sections.gridId);
    if (!inner) return false;
    sections.grid.innerHTML = inner;
    return true;
  }

  function cancelPending() {
    if (!pending) return;
    clearTimeout(pending.timer);
    if (pending.controller) pending.controller.abort();
    pending = null;
    setLoading(false);
  }

  function innerOf(html, id) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var wrapper = doc.getElementById('shopify-section-' + id);
    return wrapper ? wrapper.innerHTML : null;
  }

  function setTitle(bannerEl) {
    var h1 = bannerEl.querySelector('h1');
    if (!h1) return;
    // Drop the screen-reader-only "Collection: " prefix Dawn puts in the heading.
    var copy = h1.cloneNode(true);
    copy.querySelectorAll('.visually-hidden').forEach(function (el) { el.remove(); });
    var name = copy.textContent.replace(/\s+/g, ' ').trim();
    if (!name) return;
    var parts = document.title.split(' – ');
    var suffix = parts.length > 1 ? parts[parts.length - 1] : '';
    document.title = suffix && name !== suffix ? name + ' – ' + suffix : name;
  }

  function headerOffset() {
    var value = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return isNaN(value) ? 0 : value;
  }

  function settle(sections) {
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(sections.banner);
      initializeScrollAnimationTrigger(sections.grid);
    }
    var top = sections.banner.getBoundingClientRect().top;
    if (top < 0) {
      // Land below the sticky header, which reappears on scroll-up.
      window.scrollTo({
        top: Math.max(0, window.pageYOffset + top - headerOffset()),
        behavior: reduceMotion ? 'auto' : 'smooth',
      });
    }
    var h1 = sections.banner.querySelector('h1');
    if (h1) {
      if (!h1.hasAttribute('tabindex')) h1.setAttribute('tabindex', '-1');
      h1.focus({ preventScroll: true });
    }
  }

  function fallback(url, push) {
    setLoading(false);
    if (push) location.assign(url);
    else location.reload();
  }

  /*
    path: pathname to show; searchParams: query string without "?";
    push: true for clicks (new history entry), false for Back/Forward.
  */
  function navigate(path, searchParams, push) {
    var sections = getSections();
    var fullUrl = path + (searchParams ? '?' + searchParams : '');
    if (!sections) {
      location.assign(fullUrl);
      return;
    }

    cancelPending();
    closePanel();
    var job = {
      controller: window.AbortController ? new AbortController() : null,
      timer: null,
      timedOut: false,
    };
    pending = job;
    job.timer = setTimeout(function () {
      job.timedOut = true;
      if (job.controller) job.controller.abort();
      if (pending === job) {
        pending = null;
        fallback(fullUrl, push);
      }
    }, TIMEOUT_MS);
    setLoading(true);

    var fetchUrl =
      path + '?sections=' + encodeURIComponent(sections.bannerId + ',' + sections.gridId) +
      (searchParams ? '&' + searchParams : '');

    fetch(fetchUrl, job.controller ? { signal: job.controller.signal } : {})
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        // Follow a Shopify URL redirect: record the department actually rendered.
        if (response.redirected && response.url) {
          var landed = new URL(response.url);
          if (landed.origin === location.origin && COLLECTION_PATH.test(landed.pathname)) {
            path = landed.pathname.replace(/\/$/, '');
            fullUrl = path + (searchParams ? '?' + searchParams : '');
          }
        }
        return response.json();
      })
      .then(function (json) {
        if (pending !== job) return; // superseded
        // Back/Forward moved on while this was loading: don't paint a stale department.
        if (!push && location.pathname.replace(/\/$/, '') !== path) {
          cancelPending();
          return;
        }
        var bannerHtml = json && json[sections.bannerId] && innerOf(json[sections.bannerId], sections.bannerId);
        var gridRaw = json && json[sections.gridId];
        if (!bannerHtml || !gridRaw || gridRaw.indexOf('ProductGridContainer') === -1) {
          throw new Error('Unexpected section response');
        }

        clearTimeout(job.timer);
        pending = null;
        swapGeneration += 1;
        closePanel();
        sections.banner.innerHTML = bannerHtml;
        if (!applyGrid(sections, gridRaw)) {
          // The banner is already new; load the page normally rather than leave it mixed.
          fallback(fullUrl, push);
          return;
        }
        setLoading(false);

        if (push) {
          history.pushState({ searchParams: searchParams, sofiPath: path }, '', fullUrl);
        } else if (location.pathname !== path) {
          history.replaceState({ searchParams: searchParams, sofiPath: path }, '', fullUrl);
        }
        renderedPath = path;
        syncFacets(searchParams);
        setTitle(sections.banner);
        settle(sections);
      })
      .catch(function (error) {
        if (pending !== job) return; // superseded, cancelled or already timed out
        clearTimeout(job.timer);
        pending = null;
        if (error && error.name === 'AbortError' && !job.timedOut) {
          setLoading(false);
          return;
        }
        fallback(fullUrl, push);
      });
  }

  function keepSortOnly() {
    var current = new URLSearchParams(location.search);
    var next = new URLSearchParams();
    if (current.get('sort_by')) next.set('sort_by', current.get('sort_by'));
    return next.toString();
  }

  // Page links (1, 2, 3, ›) under the grid: load that page in place through facets.js.
  function onPageLink(event, pageLink) {
    if (!hasFacets() || pending) return false;
    var url;
    try {
      url = new URL(pageLink.href, location.href);
    } catch (e) {
      return false;
    }
    if (url.origin !== location.origin || url.pathname.replace(/\/$/, '') !== renderedPath) return false;
    var params = new URLSearchParams(url.search);
    params.delete('section_id');
    params.delete('sections');
    event.preventDefault();
    FacetFiltersForm.renderPage(params.toString());
    var grid = document.getElementById('ProductGridContainer');
    if (grid) {
      var top = grid.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollTo({
          top: Math.max(0, window.pageYOffset + top - headerOffset() - 16),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      }
    }
    return true;
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var pageLink = event.target.closest && event.target.closest(PAGE_LINK_SELECTOR);
    if (pageLink) {
      onPageLink(event, pageLink);
      return;
    }
    var link = event.target.closest && event.target.closest(LINK_SELECTOR);
    if (!link || (link.target && link.target !== '_self') || link.hasAttribute('download')) return;

    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (e) {
      return;
    }
    if (url.origin !== location.origin || !COLLECTION_PATH.test(url.pathname)) return;

    event.preventDefault();
    var path = url.pathname.replace(/\/$/, '');
    if (path === renderedPath && !url.search) {
      // Already showing this department. If it is filtered or on a later page, the link
      // means "start this department over": reload it in place with sort only.
      var here = new URLSearchParams(location.search);
      here.delete('sort_by');
      if (here.toString() === '') {
        cancelPending();
        closePanel();
        return;
      }
      navigate(path, keepSortOnly(), true);
      return;
    }
    var params = url.search ? url.search.slice(1) : keepSortOnly();
    navigate(path, params, true);
  });

  window.addEventListener('popstate', function () {
    var here = location.pathname.replace(/\/$/, '');
    if (here === renderedPath) {
      // Same department (filter steps belong to facets.js); drop any department load in flight.
      cancelPending();
      return;
    }
    if (!COLLECTION_PATH.test(location.pathname)) {
      location.reload();
      return;
    }
    navigate(here, location.search.slice(1), false);
  });

  // Back/forward cache restore: never come back dimmed or with a request pending.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    cancelPending();
    setLoading(false);
  });
})();
